import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthService } from 'src/app/services/auth.service';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import moment from 'moment';
import { LoggerService } from '../../../services/logger.service';

@Component({
  selector: 'app-square-terminal-payment',
  templateUrl: './square-terminal-payment.component.html',
  styleUrls: ['./square-terminal-payment.component.css']
})

export class SquareTerminalPaymentComponent implements OnInit {
  amount: number;
  currency: string;
  studioId: string;
  enrollmentIds: string[] = [];
  studentAccountId: string | null = null;
  paymentStatus: 'SUCCESS' | 'PENDING' | 'FAILURE' | 'CANCELED' | null = null;
  paymentStatusMessage: string = '';
  isProcessing: boolean = false;
  checkoutId: string | null = null;
  // Local payment entity id created before the charge; passed back to the
  // payment form so the receipt can send the receipt email.
  localPaymentId: number | null = null;
  paymentForm: FormGroup | null = null;
  savedCardId: string | null = null;
  idempotencyKey: string = '';
  private pollingInterval: any;

  constructor(
    private drupalRESTService: DrupalRESTService,
    private snackBar: MatSnackBar,
    public dialogRef: MatDialogRef<SquareTerminalPaymentComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    LoggerService.log('SquareTerminalPaymentComponent received data:', data);
    this.amount = data.amount;
    this.currency = data.currency || 'USD';
    this.studioId = data.studioId;
    this.enrollmentIds = data.enrollmentIds || [];
    this.studentAccountId = data.studentAccountId || null;
    this.paymentForm = data.paymentForm || null;
    this.savedCardId = data.savedCardId || null;
    this.idempotencyKey = data.idempotencyKey || '';
  }

  ngOnInit() {
    if (!this.amount || this.amount <= 0 || !this.studioId) {
      LoggerService.error('Invalid amount or studio ID provided:', { amount: this.amount, studioId: this.studioId });
      this.snackBar.open('Invalid amount or studio ID provided', 'Close', { duration: 5000 });
      this.dialogRef.close({ status: 'ERROR', message: 'Invalid amount or studio ID' });
    } else {
      LoggerService.log('SquareTerminalPaymentComponent initialized with:', { amount: this.amount, currency: this.currency, studioId: this.studioId });

      // Automatically start the payment process
      this.capturePayment();
    }
  }

  onCapturePaymentClick() {
    LoggerService.log('Capture payment clicked');
    this.capturePayment();
  }

  onCancelPaymentClick() {
    LoggerService.log('Cancel payment clicked');
    this.cancelPayment();
  }

  private capturePayment() {
    if (this.isProcessing) {
      return;
    }

    this.isProcessing = true;
    this.paymentStatus = null;
    this.paymentStatusMessage = '';
    this.checkoutId = null;

    const amountInCents = Math.round(this.amount * 100);

    if (!this.studioId) {
      this.handlePaymentError(new Error('No studio ID available'));
      return;
    }

    if (!this.enrollmentIds.length) {
      this.handlePaymentError(new Error('No enrollment IDs provided'));
      return;
    }

    const endpoint = `/api/square/terminal/checkout/${this.studioId}/${amountInCents}/${this.currency}`;
    
    // Format the payment form data to match the standard format
    const paymentFormData = { ...this.paymentForm.value };
    
    // Format date field the same way as payments-entity-form component
    if (paymentFormData.field_date_and_time) {
      paymentFormData.field_date_and_time = moment(paymentFormData.field_date_and_time.toString()).format('YYYY-MM-DD[T]HH:mm:ss');
    }
    
    const payload = {
      enrollmentIds: this.enrollmentIds,
      studentAccountId: this.studentAccountId,
      currency: this.currency,
      amount: amountInCents,
      paymentForm: paymentFormData,
      savedCardId: this.savedCardId
    };

    this.drupalRESTService.httpPOST(endpoint, payload).subscribe({
      next: (response) => {
        this.handlePaymentSuccess(response);
        // Start polling after successful checkout creation
        this.startPolling();
      },
      error: (error) => this.handlePaymentError(error)
    });
  }

  private cancelPayment() {
    if (!this.checkoutId || this.paymentStatus !== 'PENDING') {
      return;
    }

    this.isProcessing = true;

    if (!this.studioId) {
      this.handleCancellationError(new Error('No studio ID available'));
      return;
    }

    const endpoint = `/api/square/terminal/cancel/${this.studioId}/${this.checkoutId}`;

    // Remove query parameters from the URL
    this.drupalRESTService.httpPOST(endpoint, {}).subscribe({
      next: (response) => this.handleCancellationSuccess(response),
      error: (error) => this.handleCancellationError(error)
    });
  }

  private handlePaymentSuccess(response: any) {
    this.isProcessing = false;
    if (response?.id) {
      this.localPaymentId = response.id;
    }

    // Handle Terminal Checkout response
    if (response.checkout && response.checkout.id) {
      this.checkoutId = response.checkout.id;
      this.paymentStatus = response.checkout.status === 'PENDING' ? 'PENDING' : 'SUCCESS';
      this.paymentStatusMessage = `Terminal checkout created. ID: ${this.checkoutId}`;
      this.snackBar.open('Terminal checkout created successfully!', 'Close', { duration: 5000 });
    }
    // Handle Direct Payment response
    else if (response.success && response.payment) {
      this.paymentStatus = response.payment.status === 'COMPLETED' ? 'SUCCESS' : 'PENDING';
      this.checkoutId = response.payment.id;
      this.paymentStatusMessage = `Payment processed successfully. ID: ${this.checkoutId}`;
      this.snackBar.open('Payment processed successfully!', 'Close', { duration: 5000 });
      this.onClose(); // Close the dialog since payment is complete
    }
    else {
      this.handlePaymentError(new Error('Invalid response from server'));
    }
  }

  /**
   * Staff-facing text for the Square error codes that actually come up.
   * Anything unmapped falls back to Square's own `detail` string.
   */
  private static readonly SQUARE_ERROR_MESSAGES: { [code: string]: string } = {
    TRANSACTION_LIMIT: 'Card declined: the amount exceeds the limit on this card. Try a smaller amount or a different card.',
    PAYMENT_LIMIT_EXCEEDED: 'Card declined: the amount exceeds the limit on this card. Try a smaller amount or a different card.',
    INSUFFICIENT_FUNDS: 'Card declined: insufficient funds.',
    CARD_DECLINED: 'Card declined by the issuing bank.',
    GENERIC_DECLINE: 'Card declined by the issuing bank.',
    CARD_DECLINED_VERIFICATION_REQUIRED: 'Card declined: the bank needs to verify this payment. Ask the customer to contact their bank.',
    CARD_EXPIRED: 'Card declined: the card has expired.',
    INVALID_EXPIRATION: 'Card declined: the expiration date is invalid.',
    CVV_FAILURE: 'Card declined: the security code (CVV) did not match.',
    ADDRESS_VERIFICATION_FAILURE: 'Card declined: the billing postal code did not match.',
    CARD_NOT_SUPPORTED: 'Card declined: this card type is not supported for this payment.',
    ALLOWABLE_PIN_TRIES_EXCEEDED: 'Card declined: too many PIN attempts. The customer must contact their bank.',
    DEVICE_UNAVAILABLE: 'The Square Terminal is offline or unavailable. Check that the device is powered on and connected.',
    TEMPORARY_ERROR: 'Square had a temporary problem. Please try again.',
  };

  /**
   * Turn a Square errors array into something a studio can act on.
   *
   * Previously every non-authentication error collapsed to "please try again",
   * so a declined card read as a system glitch and staff re-ran it repeatedly.
   */
  private describeSquareErrors(errors: any[]): string {
    const authError = errors.find(e =>
      e.category === 'AUTHENTICATION_ERROR' ||
      e.code === 'UNAUTHORIZED' ||
      e.detail?.includes('could not be authorized')
    );

    if (authError) {
      return 'Square authentication failed. Your Square account may need to be reauthorized.';
    }

    const first = errors[0];

    return SquareTerminalPaymentComponent.SQUARE_ERROR_MESSAGES[first?.code]
      || first?.detail
      || 'Failed to create terminal checkout. Please try again.';
  }

  private handlePaymentError(error: any) {
    this.isProcessing = false;
    this.paymentStatus = 'FAILURE';

    // Check for validation errors from payment processing
    if (error?.error?.error && typeof error.error.error === 'string') {
      this.paymentStatusMessage = error.error.error;
    }
    // Check for direct error message
    else if (error?.error && typeof error.error === 'string') {
      this.paymentStatusMessage = error.error;
    }
    // Check for the specific error message about no device ID
    else if (error?.error?.error === 'No device ID configured for this studio.') {
      this.paymentStatusMessage = 'No Square Terminal device is configured for this studio. Please contact your administrator to set up a device.';
    } else if (error?.error?.errorCode === 'NO_DEVICE_ID') {
      this.paymentStatusMessage = 'No Square Terminal device is configured for this studio. Please contact your administrator to set up a device.';
    }
    // Card declines, device problems and auth failures all arrive as a Square
    // errors array. Report the actual reason rather than a generic retry.
    else if (Array.isArray(error?.error?.errors) && error.error.errors.length) {
      this.paymentStatusMessage = this.describeSquareErrors(error.error.errors);
    } else {
      this.paymentStatusMessage = 'Failed to create terminal checkout. Please try again.';
    }

    LoggerService.error('Error creating terminal checkout:', error);
    this.snackBar.open(this.paymentStatusMessage, 'Close', { duration: 10000 });
  }

  private handleCancellationSuccess(response: any) {
    this.isProcessing = false;
    this.paymentStatus = 'CANCELED';
    this.paymentStatusMessage = 'Terminal checkout cancelled successfully.';
    this.snackBar.open(this.paymentStatusMessage, 'Close', { duration: 5000 });
  }

  private handleCancellationError(error: any) {
    this.isProcessing = false;
    this.paymentStatusMessage = 'Failed to cancel terminal checkout. Please try again.';
    LoggerService.error('Error cancelling terminal checkout:', error);
    this.snackBar.open(this.paymentStatusMessage, 'Close', { duration: 5000 });
  }

  onClose() {
    this.dialogRef.close({
      status: this.paymentStatus,
      checkoutId: this.checkoutId,
      id: this.localPaymentId,
      error: this.paymentStatus === 'FAILURE' ? this.paymentStatusMessage : null
    });
  }

  private startPolling() {
    this.pollingInterval = setInterval(() => {
      if (!this.checkoutId || !this.studioId) return;

      const endpoint = `/api/square/terminal/status/${this.studioId}/${this.checkoutId}`;
      this.drupalRESTService.httpGET(endpoint).subscribe({
        next: (response) => {
          switch (response?.['status']) {
            case 'COMPLETED':
              this.paymentStatus = 'SUCCESS';
              this.paymentStatusMessage = 'Payment completed successfully';
              this.stopPolling();
              this.onClose();
              break;
            case 'CANCELED':
              this.paymentStatus = 'CANCELED';
              this.paymentStatusMessage = 'Payment was canceled';
              this.stopPolling();
              this.onClose();
              break;
            case 'FAILED':
              this.paymentStatus = 'FAILURE';
              this.paymentStatusMessage = 'Payment failed';
              this.stopPolling();
              this.onClose();
              break;
            // Payment is still processing
            case 'IN_PROGRESS':
            case 'PENDING':
              this.paymentStatus = 'PENDING';
              this.paymentStatusMessage = 'Payment is processing...';
              break;
          }
        },
        error: (error) => {
          LoggerService.error('Error polling payment status:', error);
          this.paymentStatus = 'FAILURE';
          this.paymentStatusMessage = 'Error checking payment status';
        }
      });
    }, 2000);
  }

  private stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  ngOnDestroy() {
    this.stopPolling();
  }
}
